import { supabase } from '../../shared/supabase/supabaseClient';
import type { SyncQueueItem } from './syncTypes';
import {
  interpretJsonbCommandResult,
  toFlushErr,
  toFlushOk,
  type FlushRpcResult,
  type SupabaseRpcError,
} from './rpcHelpers';
import {
  markLocalExpenseSynced,
  markLocalExpenseSettlementSynced,
  markLocalCashOutflowSynced,
  markLocalExternalServiceSynced,
  markLocalPurchaseOrderSynced,
  markLocalPurchaseReceptionSynced,
  markLocalPurchaseReturnSynced,
} from './localSyncState';
import { markLocalExpenseAttachmentSynced } from '../../expenses/infra/expenseAttachmentsRepo';

const RPC_SCHEMA: Record<string, string> = {
  apply_cash_outflow_void_command: 'pos',
  apply_expense_command: 'pos',
  apply_expense_void_command: 'pos',
  apply_expense_attachment_command: 'pos',
  apply_expense_settlement_command: 'pos',
  apply_expense_settlement_void_command: 'pos',
  apply_promotion_command: 'pos',
  apply_promotion_event_command: 'pos',
  apply_purchase_order_command: 'pos',
  apply_purchase_reception_command: 'pos',
  apply_purchase_reception_with_expense_command: 'pos',
  apply_purchase_reception_void_command: 'pos',
  apply_purchase_return_command: 'pos',
  apply_purchase_return_void_command: 'pos',
  apply_stock_transfer_command: 'pos',
  void_stock_transfer_command: 'pos',
  apply_stock_lot_command: 'inventory',
  apply_stock_lot_allocation_command: 'inventory',
  apply_customer_command: 'customers',
  apply_external_service_command: 'integrations',
  sync_apply_stock_adjustment: 'public',
  sync_apply_stock_transfer: 'public',
  sync_apply_physical_count_batch: 'public',
  apply_product_variant_command: 'public',
  apply_category_command: 'public',
  apply_product_catalog_event_command: 'public',
  apply_brand_command: 'public',
  apply_supplier_command: 'public',
  apply_branch_settings_command: 'public',
  apply_store_profile_command: 'public',
  apply_scheduled_closure_command: 'public',
  update_kitchen_ticket_status: 'public',
}

function rpcCall(name: string, args: Record<string, unknown>) {
  const schema = RPC_SCHEMA[name] ?? 'public'
  return supabase.schema(schema).rpc(name, args)
}

function decodeBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export const applyCashOutflowVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_cash_outflow_void_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const outflowId = String(item.payload?.outflowId ?? '').trim();
  if (outflowId) await markLocalCashOutflowSynced(outflowId);
  return toFlushOk();
};

export const applyExpenseViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_expense_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const expenseId = String(item.payload?.expenseId ?? '').trim();
  if (expenseId) await markLocalExpenseSynced(expenseId);
  return toFlushOk();
};

export const applyExpenseVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_expense_void_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const expenseId = String(item.payload?.expenseId ?? '').trim();
  if (expenseId) await markLocalExpenseSynced(expenseId);
  return toFlushOk();
};

export const applyExpenseAttachmentViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const payload = item.payload as {
    attachmentId?: string;
    expenseId?: string;
    branchId?: string;
    fileName?: string;
    mimeType?: string;
    fileSize?: number;
    fileBase64?: string;
    orgId?: string;
    createdAt?: string;
    createdBy?: string;
  };

  const attachmentId = String(payload.attachmentId ?? '');
  const orgId = String(payload.orgId ?? '');
  const expenseId = String(payload.expenseId ?? '');
  const fileName = String(payload.fileName ?? 'comprobante');
  const mimeType = String(payload.mimeType ?? 'application/octet-stream');
  const fileBase64 = String(payload.fileBase64 ?? '');

  if (!attachmentId || !orgId || !expenseId || !fileBase64) {
    return { ok: false, error: 'expense_attachment payload inválido' };
  }

  const ext = mimeType.startsWith('image/png')
    ? 'png'
    : mimeType.startsWith('image/jpeg') || mimeType.startsWith('image/jpg')
      ? 'jpg'
      : 'bin';
  const storagePath = `${orgId}/${expenseId}/${attachmentId}.${ext}`;

  try {
    const { error: uploadError } = await supabase.storage
      .from('expense-receipts')
      .upload(storagePath, decodeBase64(fileBase64), {
        contentType: mimeType,
        upsert: true,
      });

    if (uploadError) return toFlushErr({ message: uploadError.message });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, error: `storage upload failed: ${message}` };
  }

  const fileUrl = storagePath;

  const { error } = await rpcCall('apply_expense_attachment_command', {
    p_operation: 'insert',
    p_payload: {
      ...payload,
      fileUrl,
      fileBase64: undefined,
    },
  });
  if (error) return toFlushErr(error);
  await markLocalExpenseAttachmentSynced(attachmentId, fileUrl);
  return toFlushOk();
};

export const callExpenseSettlementRpc = async (
  payload: Record<string, unknown>
): Promise<SupabaseRpcError | null> => {
  const { error } = await rpcCall('apply_expense_settlement_command', {
    p_payload: payload,
  });
  return error;
};

export const applyExpenseSettlementVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_expense_settlement_void_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const settlementId = String(item.payload?.settlementId ?? '').trim();
  if (settlementId) await markLocalExpenseSettlementSynced(settlementId);
  return toFlushOk();
};

export const applyStockAdjustmentViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('sync_apply_stock_adjustment', { p_payload: item.payload });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyStockTransferViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('sync_apply_stock_transfer', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyStockLotViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_stock_lot_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyStockLotAllocationViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_stock_lot_allocation_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyCustomerViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_customer_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyProductVariantViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_product_variant_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyCategoryViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_category_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyProductCatalogEventViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_product_catalog_event_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyBrandViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_brand_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applySupplierViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_supplier_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyExternalServiceViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_external_service_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const operationId = String(item.payload?.operationId ?? '').trim();
  if (operationId) await markLocalExternalServiceSynced(operationId);
  return toFlushOk();
};

export const applyBranchSettingsViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_branch_settings_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyStoreProfileViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_store_profile_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyScheduledClosureViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_scheduled_closure_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyPromotionViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { data, error } = await rpcCall('apply_promotion_command', {
    p_operation: item.operation,
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return interpretJsonbCommandResult(data);
};

export const applyPromotionEventViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { data, error } = await rpcCall('apply_promotion_event_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return interpretJsonbCommandResult(data);
};

export const applyPurchaseOrderViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_order_command', {
    p_operation: 'upsert',
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const purchaseOrderId = String(item.payload?.purchaseOrderId ?? '').trim();
  if (purchaseOrderId) await markLocalPurchaseOrderSynced(purchaseOrderId);
  return toFlushOk();
};

export const applyPurchaseOrderVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_order_command', {
    p_operation: 'void',
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const purchaseOrderId = String(item.payload?.purchaseOrderId ?? '').trim();
  if (purchaseOrderId) await markLocalPurchaseOrderSynced(purchaseOrderId);
  return toFlushOk();
};

export const applyPurchaseOrderCloseViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_order_command', {
    p_operation: 'close',
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const purchaseOrderId = String(item.payload?.purchaseOrderId ?? '').trim();
  if (purchaseOrderId) await markLocalPurchaseOrderSynced(purchaseOrderId);
  return toFlushOk();
};

export const applyPurchaseReceptionViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_reception_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const receptionId = String(item.payload?.receptionId ?? '').trim();
  if (receptionId) await markLocalPurchaseReceptionSynced(receptionId);
  return toFlushOk();
};

export const applyPurchaseReceptionWithExpenseViaRpc = async (
  item: SyncQueueItem,
): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_reception_with_expense_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const receptionId = String(item.payload?.receptionId ?? '').trim();
  if (receptionId) await markLocalPurchaseReceptionSynced(receptionId);
  const linkedExpense = item.payload?.linkedExpense as Record<string, unknown> | undefined;
  const expenseId = String(linkedExpense?.expenseId ?? '').trim();
  if (expenseId) await markLocalExpenseSynced(expenseId);
  return toFlushOk();
};

export const applyPurchaseReceptionVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_reception_void_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const receptionId = String(item.payload?.receptionId ?? '').trim();
  if (receptionId) await markLocalPurchaseReceptionSynced(receptionId);
  return toFlushOk();
};

export const applyPurchaseReturnViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_return_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const returnId = String(item.payload?.returnId ?? '').trim();
  if (returnId) await markLocalPurchaseReturnSynced(returnId);
  return toFlushOk();
};

export const applyPurchaseReturnVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('apply_purchase_return_void_command', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  const returnId = String(item.payload?.returnId ?? '').trim();
  if (returnId) await markLocalPurchaseReturnSynced(returnId);
  return toFlushOk();
};

/**
 * Legacy adapter: `branch_transfer` (Compras) → `sync_apply_stock_transfer` (Inventory).
 * `pos.apply_stock_transfer_command` was dropped in 20261730130000. Live intake now
 * calls Inventory `transferStock` directly; this remains for any queued legacy items.
 */
export const applyBranchTransferViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const payload = (item.payload ?? {}) as Record<string, unknown>;
  const fromBranchId = String(payload.fromBranchId ?? '').trim();
  const toBranchId = String(payload.toBranchId ?? '').trim();
  const performedAt = String(payload.transferredAt ?? payload.performedAt ?? '').trim() || new Date().toISOString();
  const rawItems = Array.isArray(payload.items) ? payload.items : null;

  const lines: Array<Record<string, unknown>> =
    rawItems && rawItems.length > 0
      ? (rawItems as Array<Record<string, unknown>>)
      : [
          {
            productId: payload.productId,
            quantity: payload.quantity,
            itemId: payload.transferId ?? payload.id,
          },
        ];

  for (const line of lines) {
    const productId = String(line.productId ?? '').trim();
    const quantity = Number(line.quantity ?? 0);
    const lineId = String(line.itemId ?? payload.transferId ?? '').trim();
    if (!productId || !fromBranchId || !toBranchId || !(quantity > 0) || !lineId) {
      return { ok: false, error: 'branch_transfer_payload_invalid' };
    }
    const { error } = await rpcCall('sync_apply_stock_transfer', {
      p_operation: 'insert',
      p_payload: {
        transferId: lineId,
        operationId: lineId,
        productId,
        fromBranchId,
        toBranchId,
        quantity,
        status: 'completed',
        performedAt,
        unitCost: line.unitCost ?? null,
        variantId: line.variantId ?? null,
      },
    });
    if (error) return toFlushErr(error);
  }
  return toFlushOk();
};

/** Legacy adapter: reject pending transfer via Inventory motor. */
export const applyBranchTransferVoidViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const payload = (item.payload ?? {}) as Record<string, unknown>;
  const transferId = String(payload.transferId ?? '').trim();
  if (!transferId) return { ok: false, error: 'transferId_required' };
  const { error } = await rpcCall('sync_apply_stock_transfer', {
    p_operation: 'update',
    p_payload: {
      transferId,
      operationId: `${transferId}:void`,
      productId: payload.productId,
      fromBranchId: payload.fromBranchId,
      toBranchId: payload.toBranchId,
      quantity: payload.quantity,
      status: 'rejected',
      performedAt: payload.voidedAt ?? new Date().toISOString(),
      unitCost: payload.unitCost ?? null,
      variantId: payload.variantId ?? null,
      performedByUserId: payload.voidedBy ?? null,
    },
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyKitchenBumpViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const ticketId = String(item.payload?.ticketId ?? '').trim();
  const status = String(item.payload?.status ?? '').trim();
  if (!ticketId || !status) {
    return { ok: false, error: 'Faltan ticketId o status para kitchen_bump' };
  }
  const { error } = await rpcCall('update_kitchen_ticket_status', {
    p_ticket_id: ticketId,
    p_status: status,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};

export const applyPhysicalCountBatchViaRpc = async (item: SyncQueueItem): Promise<FlushRpcResult> => {
  const { error } = await rpcCall('sync_apply_physical_count_batch', {
    p_payload: item.payload,
  });
  if (error) return toFlushErr(error);
  return toFlushOk();
};
